// packages/web/src/lib/displayMode.ts — CV5 §1: IS THERE A WAY BACK OUTSIDE
// THIS WINDOW?
//
// The manifest declares `display: "standalone"` and `scope: "/"`, so an
// INSTALLED Vowric has no toolbar, no back button, and no swipe-back. In that
// window a same-origin `target="_blank"` does NOT open a tab — iOS navigates
// the app's own window to the URL, and the only escape is force-quitting the
// app. CV4 repointed "Open original" from a dead `blob:` URL at a real
// same-origin route and so turned a harmlessly broken button into a trap
// (Huan's installed-PWA walk, 2026-08-16).
//
// The rule this module exists to enforce: A SAME-ORIGIN LINK MAY ONLY
// TOP-LEVEL-NAVIGATE WHERE A BROWSER CAN BRING THE USER BACK. Cross-origin
// URLs (a Telegram deep link, a maps search, IMDb) are a different case
// entirely and are NOT gated here: iOS hands those to another app or to
// Safari, and the PWA survives underneath.
//
// Read at call time, never cached: a page can be launched in a tab and later
// installed, and `display-mode` also changes when a PWA is opened from the
// home screen in the same session.

/** True when this document is running as an installed app — no browser
 * chrome, so nothing can bring the user back from a navigation. Also true
 * for `fullscreen`/`minimal-ui`, which are equally chrome-less on iOS. Any
 * environment without `matchMedia` (jsdom, SSR) answers false: a browser tab
 * is the safe assumption, because the affordance it enables is the one that
 * already worked. */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  for (const mode of ['standalone', 'fullscreen', 'minimal-ui']) {
    try {
      if (window.matchMedia(`(display-mode: ${mode})`).matches) return true;
    } catch {
      /* a jsdom stub that throws on an unknown feature is not a standalone window */
    }
  }
  // iOS Safari's own pre-standard flag, still the only truthful signal on
  // older iOS versions when a PWA is launched from the home screen.
  const legacy = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return legacy === true;
}
